import React, { useContext, useEffect, useState } from 'react';

import styled from '@emotion/styled';

import { paymentLinkActions } from '../../redux/slices/paymentLinkSlice';
import { useAppDisPatch, useAppSelector } from '../../redux/store';
import CancelIcon from '../Icons/CancelIcon';
import { Modal } from '../Modal/Modal';
import { ModalContext } from '../Modal/ModalContext';

interface PaystackIframeProps {
  paymentLink: string;
}

const PaystackIframe: React.FC<PaystackIframeProps> = ({ paymentLink }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const dispatch = useAppDisPatch();
  const modalContext = useContext<any>(ModalContext);

  const handleCancelPaystackPopUp = (event: any) => {
    dispatch(paymentLinkActions.setPaymentData(undefined));
    modalContext.setShowModal(false);
  };

  return (
    <Container>
      <Iframe
        src={paymentLink}
        title='Paystack Payment'
        frameBorder='0'
        allowFullScreen
        onLoad={() => setIsLoaded(true)}
        style={{ opacity: isLoaded ? 1 : 0 }}
      ></Iframe>
      <IconContainer onClick={handleCancelPaystackPopUp}>
        <CancelIcon />
      </IconContainer>
    </Container>
  );
};

interface PaystackPaymentModalProps {
  onSuccess?: () => void;
}

const PaystackPaymentModal = ({ onSuccess }: PaystackPaymentModalProps) => {
  const [showModal, setShowModal] = useState(false);
  const { paymentData } = useAppSelector((state) => state.paymentData);
  const dispatch = useAppDisPatch();
  const modalContext = useContext<any>(ModalContext);

  useEffect(() => {
    const handlePaymentMessage = (event: any) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      const isLegacySuccess = event.data === 'paymentCompleted';
      const isStructuredSuccess =
        event.data?.source === 'logirate-paystack' &&
        event.data?.type === 'PAYMENT_COMPLETED';

      if (isLegacySuccess || isStructuredSuccess) {
        dispatch(paymentLinkActions.setPaymentData(undefined));
        modalContext?.setShowModal(false);
        setShowModal(false);
        onSuccess?.();
      }
    };

    window.addEventListener('message', handlePaymentMessage);

    return () => {
      window.removeEventListener('message', handlePaymentMessage);
    };
  }, [dispatch, modalContext, onSuccess]);

  useEffect(() => {
    if (paymentData) {
      setShowModal(true);
    } else {
      setShowModal(false);
    }
  }, [paymentData]);

  return (
    <Modal showModal={showModal} setShowModal={setShowModal}>
      <PaystackIframe paymentLink={paymentData?.authorizationUrl || ''} />
    </Modal>
  );
};

export default PaystackPaymentModal;

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  display: grid;
  justify-content: center;

  & > * {
    grid-column: 1;
    grid-row: 1;
  }
`;

const Iframe = styled.iframe`
  width: 535px;
  height: 100%;
  transition: opacity 0.2s ease-in;
`;

const IconContainer = styled.div`
  margin-top: 90px;
  margin-left: auto;
  cursor: pointer;

  & > svg {
    transform: scale(100%);
    transition: transform 0.3s ease-out;

    &:hover {
      transform: scale(110%);
    }
  }
`;
